import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, of, Subject } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  flattenTemplateSections,
  moveInList,
  parseStandardTexts,
  TemplateSectionDraft,
  toTemplateSections,
} from '../safety-round.logic';
import {
  RegulatoryReference,
  SafetyRoundDueDays,
  SafetyRoundTemplateAssignment,
  SafetyRoundTemplateDetail,
  SafetyRoundTemplateItem,
  SafetyRoundTemplateStatus,
  SaveSafetyRoundTemplateRequest,
} from '../safety-round.models';
import { FlexiSafetyRoundTemplateService } from './safety-round-template.service';

/** The one checkpoint open in its inline edit form. */
interface CheckpointEdit {
  area: number;
  index: number;
  /** A checkpoint added just now; Cancel removes it again. */
  isNew: boolean;
  title: string;
  guidance: string;
  /** One standard text per line. */
  standardTexts: string;
  references: RegulatoryReference[];
  photoAt3: boolean;
  photoAt4: boolean;
}

/**
 * Admin mode › Flexi › Settings › Vernerundemaler › one template.
 *
 * Metadata and suggested deadlines, then one card per area with its
 * checkpoints. One area is open at a time and one checkpoint is edited at a
 * time. Save replaces the whole template; Publish saves and then raises the
 * version. Started rounds keep the checkpoints they were started with, so
 * nothing here changes a round in progress.
 */
@Component({
  selector: 'flexi-safety-round-template-editor',
  templateUrl: './safety-round-template-editor.component.html',
  styleUrls: ['./safety-round-template-editor.component.scss'],
  standalone: false,
})
export class SafetyRoundTemplateEditorComponent implements OnInit, OnDestroy {
  readonly TemplateStatus = SafetyRoundTemplateStatus;

  templateId = 0;
  template: SafetyRoundTemplateDetail | null = null;
  loading = true;
  loadFailed = false;

  name = '';
  industry = '';
  description = '';
  dueDays: SafetyRoundDueDays = { ShouldImprove: 90, LegalBreach: 30, Danger: 7 };
  sections: TemplateSectionDraft[] = [];

  /** Index of the area whose checkpoints are shown. */
  openArea: number | null = 0;
  renamingArea: number | null = null;
  renameValue = '';
  editing: CheckpointEdit | null = null;
  submitted = false;

  saving = false;
  publishing = false;
  archiving = false;
  togglingDefault = false;

  assignments: SafetyRoundTemplateAssignment[] = [];
  assignmentsLoading = false;
  assignOpen = false;
  removingIds = new Set<number>();

  /** The request as last loaded or saved — the dirty check compares against it. */
  private savedSnapshot = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiSafetyRoundTemplateService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private confirmation: ConfirmationService,
    private notifications: NotificationService,
  ) {}

  get isArchived(): boolean {
    return this.template?.Status === SafetyRoundTemplateStatus.Archived;
  }

  get isPublished(): boolean {
    return this.template?.Status === SafetyRoundTemplateStatus.Published;
  }

  get busy(): boolean {
    return this.saving || this.publishing || this.archiving;
  }

  get isDirty(): boolean {
    return !!this.template && (this.editing !== null || JSON.stringify(this.buildRequest()) !== this.savedSnapshot);
  }

  get itemCount(): number {
    return this.sections.reduce((sum, section) => sum + section.items.length, 0);
  }

  get assignedDefaultCount(): number {
    return this.assignments.filter(a => a.IsDefault).length;
  }

  /** Why Save/Publish is blocked, or null. */
  get invalidKey(): string | null {
    if (!this.name.trim()) {
      return 'FLEXI_SAFETY_ROUNDS.EDITOR.NAME_REQUIRED';
    }
    if (this.sections.some(section => section.items.length > 0 && !section.name.trim())) {
      return 'FLEXI_SAFETY_ROUNDS.EDITOR.AREA_NAME_REQUIRED';
    }
    const days = [this.dueDays.ShouldImprove, this.dueDays.LegalBreach, this.dueDays.Danger];
    if (days.some(d => !Number.isInteger(Number(d)) || Number(d) < 1)) {
      return 'FLEXI_SAFETY_ROUNDS.EDITOR.DUE_DAYS_INVALID';
    }
    if (this.editing && !this.editing.title.trim()) {
      return 'FLEXI_SAFETY_ROUNDS.EDITOR.CHECKPOINT_TITLE_REQUIRED';
    }
    return null;
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.templateId = Number(params.get('templateId'));
      this.load();
      this.loadAssignments();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.api
      .get(this.templateId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: template => this.applyTemplate(template),
        error: () => {
          this.template = null;
          this.loadFailed = true;
        },
      });
  }

  private applyTemplate(template: SafetyRoundTemplateDetail): void {
    this.template = template;
    this.name = template.Name;
    this.industry = template.Industry ?? '';
    this.description = template.Description ?? '';
    this.dueDays = { ...template.DueDays };
    this.sections = toTemplateSections(template.Items ?? []);
    this.editing = null;
    this.renamingArea = null;
    this.submitted = false;
    if (this.openArea === null || this.openArea >= this.sections.length) {
      this.openArea = this.sections.length ? 0 : null;
    }
    this.savedSnapshot = JSON.stringify(this.buildRequest());
  }

  loadAssignments(): void {
    this.assignmentsLoading = true;
    this.api
      .businesses(this.templateId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.assignmentsLoading = false)))
      .subscribe({
        next: rows => (this.assignments = rows),
        error: () => (this.assignments = []),
      });
  }

  backToLibrary(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  private buildRequest(): SaveSafetyRoundTemplateRequest {
    return {
      Name: this.name.trim(),
      Industry: this.industry.trim() || null,
      Description: this.description.trim() || null,
      DueDays: {
        ShouldImprove: Number(this.dueDays.ShouldImprove),
        LegalBreach: Number(this.dueDays.LegalBreach),
        Danger: Number(this.dueDays.Danger),
      },
      Items: flattenTemplateSections(this.sections),
    };
  }

  /* ======================================================================
     Save, publish, archive
     ====================================================================== */

  /**
   * Applies an open checkpoint edit and saves. Emits false without a request
   * when something is invalid.
   */
  private persist(): Observable<boolean> {
    this.submitted = true;
    if (this.invalidKey) {
      return of(false);
    }
    if (this.editing) {
      this.applyEdit();
    }
    const request = this.buildRequest();
    return this.api.save(this.templateId, request).pipe(
      map(() => {
        this.savedSnapshot = JSON.stringify(request);
        return true;
      }),
    );
  }

  save(): void {
    if (this.busy || this.isArchived) {
      return;
    }
    this.saving = true;
    this.persist()
      .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
      .subscribe(saved => {
        if (saved) {
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.load();
        }
      });
  }

  /** Saves, then publishes as Version + 1. Started rounds keep their snapshot. */
  publish(): void {
    if (this.busy || this.isArchived) {
      return;
    }
    this.publishing = true;
    this.persist()
      .pipe(
        switchMap(saved => (saved ? this.api.publish(this.templateId).pipe(map(() => true)) : of(false))),
        takeUntil(this.destroy$),
        finalize(() => (this.publishing = false)),
      )
      .subscribe(published => {
        if (published) {
          this.notifications.success('FLEXI_SAFETY_ROUNDS.EDITOR.PUBLISHED');
          this.load();
        }
      });
  }

  /** Separate from Save: the backend stores it outside the template body. */
  toggleDefault(): void {
    const template = this.template;
    if (!template || !this.isPublished || this.togglingDefault) {
      return;
    }
    const next = !template.IsDefaultForNewBusinesses;
    template.IsDefaultForNewBusinesses = next;
    this.togglingDefault = true;
    this.api
      .setDefaultForNew(template.Id, next)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.togglingDefault = false)))
      .subscribe({
        next: () => this.notifications.success('UI_NOTIFICATIONS.SAVED'),
        error: () => (template.IsDefaultForNewBusinesses = !next),
      });
  }

  confirmArchive(): void {
    if (this.busy || this.isArchived) {
      return;
    }
    this.confirmation.confirm({
      key: 'fvrEditor',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.CONFIRM_ARCHIVE', { name: this.name }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.archiving = true;
        this.api
          .archive(this.templateId)
          .pipe(takeUntil(this.destroy$), finalize(() => (this.archiving = false)))
          .subscribe(() => {
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.load();
          });
      },
    });
  }

  /* ======================================================================
     Areas
     ====================================================================== */

  toggleArea(index: number): void {
    if (this.editing && this.editing.area !== index) {
      return;
    }
    this.openArea = this.openArea === index ? null : index;
  }

  addArea(): void {
    if (this.isArchived) {
      return;
    }
    this.sections = [...this.sections, { name: this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.NEW_AREA'), items: [] }];
    this.openArea = this.sections.length - 1;
    this.startRename(this.sections.length - 1);
  }

  startRename(index: number): void {
    this.renamingArea = index;
    this.renameValue = this.sections[index]?.name ?? '';
  }

  commitRename(): void {
    const index = this.renamingArea;
    if (index === null) {
      return;
    }
    const name = this.renameValue.trim();
    if (name) {
      this.sections[index].name = name;
    }
    this.renamingArea = null;
  }

  cancelRename(): void {
    this.renamingArea = null;
  }

  moveArea(index: number, delta: number): void {
    if (this.editing) {
      return;
    }
    const target = index + delta;
    this.sections = moveInList(this.sections, index, delta);
    if (this.openArea === index && target >= 0 && target < this.sections.length) {
      this.openArea = target;
    } else if (this.openArea === target) {
      this.openArea = index;
    }
  }

  removeArea(index: number): void {
    const section = this.sections[index];
    if (!section || this.editing) {
      return;
    }
    const remove = () => {
      this.sections = this.sections.filter((_, i) => i !== index);
      this.openArea = this.sections.length ? Math.min(index, this.sections.length - 1) : null;
    };
    if (!section.items.length) {
      remove();
      return;
    }
    this.confirmation.confirm({
      key: 'fvrEditor',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.CONFIRM_REMOVE_AREA', {
        name: section.name,
        count: section.items.length,
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: remove,
    });
  }

  /* ======================================================================
     Checkpoints
     ====================================================================== */

  /** "2 regelverk · 3 standardtekster · bilde ved 4" */
  itemMeta(item: SafetyRoundTemplateItem): string {
    const parts: string[] = [];
    if (item.RegulatoryReferences.length) {
      parts.push(this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.META_REGULATIONS', { count: item.RegulatoryReferences.length }));
    }
    if (item.StandardTexts.length) {
      parts.push(this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.META_STANDARD_TEXTS', { count: item.StandardTexts.length }));
    }
    if (item.RequirePhotoAtGrade3 && item.RequirePhotoAtGrade4) {
      parts.push(this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.META_PHOTO_3_4'));
    } else if (item.RequirePhotoAtGrade4) {
      parts.push(this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.META_PHOTO', { grade: 4 }));
    } else if (item.RequirePhotoAtGrade3) {
      parts.push(this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.META_PHOTO', { grade: 3 }));
    }
    return parts.join(' · ');
  }

  isEditing(area: number, index: number): boolean {
    return this.editing?.area === area && this.editing.index === index;
  }

  addItem(area: number): void {
    if (this.isArchived || this.editing) {
      return;
    }
    const section = this.sections[area];
    section.items = [...section.items, {
      SectionName: section.name,
      SectionPosition: area + 1,
      Position: section.items.length + 1,
      Title: '',
      Guidance: null,
      StandardTexts: [],
      RegulatoryReferences: [],
      RequirePhotoAtGrade3: false,
      RequirePhotoAtGrade4: false,
    }];
    this.openEdit(area, section.items.length - 1, true);
  }

  editItem(area: number, index: number): void {
    if (this.isArchived || this.editing) {
      return;
    }
    this.openEdit(area, index, false);
  }

  private openEdit(area: number, index: number, isNew: boolean): void {
    const item = this.sections[area].items[index];
    this.editing = {
      area,
      index,
      isNew,
      title: item.Title,
      guidance: item.Guidance ?? '',
      standardTexts: item.StandardTexts.join('\n'),
      references: item.RegulatoryReferences.map(ref => ({ ...ref })),
      photoAt3: item.RequirePhotoAtGrade3,
      photoAt4: item.RequirePhotoAtGrade4,
    };
    this.openArea = area;
  }

  /** Writes the edit form back into the area. */
  applyEdit(): void {
    const edit = this.editing;
    if (!edit || !edit.title.trim()) {
      return;
    }
    const section = this.sections[edit.area];
    const items = [...section.items];
    items[edit.index] = {
      ...items[edit.index],
      Title: edit.title.trim(),
      Guidance: edit.guidance.trim() || null,
      StandardTexts: parseStandardTexts(edit.standardTexts),
      RegulatoryReferences: edit.references
        .map(ref => ({ Title: ref.Title.trim(), Url: ref.Url?.trim() || null }))
        .filter(ref => ref.Title.length > 0),
      RequirePhotoAtGrade3: edit.photoAt3,
      RequirePhotoAtGrade4: edit.photoAt4,
    };
    section.items = items;
    this.editing = null;
  }

  cancelEdit(): void {
    const edit = this.editing;
    if (!edit) {
      return;
    }
    if (edit.isNew) {
      const section = this.sections[edit.area];
      section.items = section.items.filter((_, i) => i !== edit.index);
    }
    this.editing = null;
  }

  removeEditedItem(): void {
    const edit = this.editing;
    if (!edit) {
      return;
    }
    const section = this.sections[edit.area];
    section.items = section.items.filter((_, i) => i !== edit.index);
    this.editing = null;
  }

  /** Moves the checkpoint being edited, keeping its form open. */
  moveEditedItem(delta: number): void {
    const edit = this.editing;
    if (!edit) {
      return;
    }
    const section = this.sections[edit.area];
    const target = edit.index + delta;
    if (target < 0 || target >= section.items.length) {
      return;
    }
    section.items = moveInList(section.items, edit.index, delta);
    edit.index = target;
  }

  addReference(): void {
    this.editing?.references.push({ Title: '', Url: null });
  }

  removeReference(index: number): void {
    this.editing?.references.splice(index, 1);
  }

  /* ======================================================================
     Assigned businesses
     ====================================================================== */

  openAssign(): void {
    if (this.isPublished) {
      this.assignOpen = true;
    }
  }

  assignClosed(saved: boolean): void {
    this.assignOpen = false;
    if (saved) {
      this.loadAssignments();
    }
  }

  confirmUnassign(assignment: SafetyRoundTemplateAssignment): void {
    this.confirmation.confirm({
      key: 'fvrEditor',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.CONFIRM_UNASSIGN', { name: assignment.BusinessName }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.removingIds.add(assignment.BusinessId);
        this.api
          .unassign(this.templateId, assignment.BusinessId)
          .pipe(takeUntil(this.destroy$), finalize(() => this.removingIds.delete(assignment.BusinessId)))
          .subscribe(() => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            this.loadAssignments();
          });
      },
    });
  }
}
